import React, { FC, useRef, useState } from 'react'
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import Search from 'ps-components/Search/Search';
import isIE from 'ps-components/Utils/checkForIE';
import Popover from 'ps-components/Popover/Popover';
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { SLIDEOUTS } from '../../../Slideout/Slideouts';
import '../MarketplaceMatches.scss'
import { IAppState } from '../../../../lib/types/AppState';
import { setSearchText, toggleActiveSurveys } from '../MarketplaceMatchesActions';

interface Props {
  scrollRef: React.RefObject<any>
}

const MarketplaceMatchesSlideoutHeaderActions: FC<Props> = ({ scrollRef }) => {
  const dispatch = useDispatch()
  const [showAboutMatches, setShowAboutMatches] = useState(false);
  const { slideout } = useSelector((state: IAppState) => state.marketPricing.MarketplaceMatchesReducer.marketplaceMatchesReducer)
  const { showActiveSurveys, selectedJobIds, currentJobIndex, activeVisibleSurveysCount, suggestedVisibleSurveysCount, searchText } = slideout

  const aboutMatchesRef = useRef<HTMLDivElement | null>(null);

  const scrollAndSearch = (value: string) => {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0;
    }
    dispatch(setSearchText(value));
  }

  const searchOnKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      scrollAndSearch(event.target.value);
    }
  }

  const showSettings = () => {
    dispatch(showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_SETTINGS, {
      onBack: () => dispatch(showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_NEW, { selectedJobIds, startingJobIndex: currentJobIndex, isBigger: true, dontRefetch: true })),
    }));
  }

  function togglePurchased(active: boolean) {
    dispatch(toggleActiveSurveys(active));
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0;
    }
  }

  function toggleAboutMatches(e : any) {
    e?.stopPropagation(); 
    setShowAboutMatches(!showAboutMatches);
  }

  return (
    <div className="marketplace-matches__search-container">
      <Search
        className="marketplace-matches__search"
        onClick={() => scrollAndSearch('')}
        onKeyDown={searchOnKeyDown}
        onSearch={(value: string) => scrollAndSearch(value)}
        placeholder="Search Marketplace Matches"
        testId="marketplace-matches__search"
        useInternalState={false}
        onChange={(e: any) => dispatch(setSearchText(e.target.value))}
        value={searchText}
      />

      <div className={classnames('marketplace-matches__right-buttons', { ie: isIE() })}>
        <div className="marketplace-matches__links" ref={aboutMatchesRef}>
          <Link
            to="#"
            id="about-matches-link"
            className="marketplace-matches__link mpm-about-matches-link"
            onClick={toggleAboutMatches}
          >
            About Matches
          </Link>
          <div id="marketplace-matches__about-popover" className="marketplace-matches__about-popover"></div>
          <div className="marketplace-matches__seperator" />
          <Link to="#" className="marketplace-matches__link mpm-edit-link" onClick={showSettings}>
            Edit Filters
          </Link>
        </div>

        <React.Fragment>
          {showAboutMatches &&
            ReactDOM.createPortal(
              <Popover
                triggerElement={aboutMatchesRef}
                relativeElement={document.querySelector('about-matches-link')}
                display={showAboutMatches}
                togglePopover={toggleAboutMatches}
              >
                <React.Fragment>
                  <div className="marketplace-matches__about-matches-title">{'What are marketplace matches?'}</div>
                  <div className="marketplace-matches__about-matches-text">
                    Marketplace matches are additional survey jobs based on your current survey job matches for this job. These matches come from companies who selected the same matches for the same or similar job title. Edit preferences to filter results.
                  </div>
                  <div className="marketplace-matches__about-matches-title">What are frequently matched matches?</div>
                  <div className="marketplace-matches__about-matches-text">
                    Other companies with similar matches have frequently matched these survey jobs, indicating a high match likelihood.
                  </div>
                  <div className="marketplace-matches__about-matches-title">What are suggested surveys?</div>
                  <div className="marketplace-matches__about-matches-text">
                    Suggested surveys are surveys not currently in your survey library in which there may be relevant matches for the job. Click on the Suggested Surveys tab to display potential matches in other surveys.
                  </div>
                </React.Fragment>
              </Popover>,
              document.getElementById('marketplace-matches__about-popover') as Element
            )}
        </React.Fragment>
        <div className={classnames('marketplace-matches__toggle', 'left', { active: showActiveSurveys })} onClick={() => togglePurchased(true)}>
          {activeVisibleSurveysCount === null || activeVisibleSurveysCount < 1 ? 'Active Surveys' : `Active Surveys (${activeVisibleSurveysCount})`}
        </div>
        <div className={classnames('marketplace-matches__toggle', 'right', { active: !showActiveSurveys })} onClick={() => togglePurchased(false)}>
          {suggestedVisibleSurveysCount === null || suggestedVisibleSurveysCount < 1 ? 'Suggested Surveys' : `Suggested Surveys (${suggestedVisibleSurveysCount})`}
        </div>
      </div>
    </div>
  )
}

export default MarketplaceMatchesSlideoutHeaderActions